import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MainLayout from '../../components/MainLayout';
import RevisionRecursosPage from './RevisionRecursosPage';
import SolicitarCambiosDialog from './SolicitarCambiosDialog';
import { IRecursoRevision } from '../../types/miRecurso';
import { RootState } from '../../state-management/store';
import { apiErrorMessage, listCatalogSubjects } from '../../api/catalog';
import { listReviewResources, resourceDownloadUrl, setResourceStatus } from '../../api/resources';
import {
	setSearchQuery,
	setSelectedFileType,
	setSelectedStatus,
	setSelectedMateria,
	setDateRange,
	resetFilters,
} from '../../state-management/slices/RevisionRecursosPage';

const RecursosPage: React.FC = () => {
	const dispatch = useDispatch();
	const [recursos, setRecursos] = useState<IRecursoRevision[]>([]);
	const [materias, setMaterias] = useState<{ id: number; name: string }[]>([]);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [changesTarget, setChangesTarget] = useState<IRecursoRevision | null>(null);

	const { searchQuery, selectedFileType, selectedStatus, selectedMateria, dateRange } = useSelector(
		(state: RootState) => state.revisionRecursosPage
	);

	useEffect(() => {
		let cancelled = false;
		listCatalogSubjects()
			.then((rows) => {
				if (!cancelled) setMaterias(rows.map((subject) => ({ id: subject.id, name: subject.name })));
			})
			.catch((error: unknown) => {
				if (!cancelled) setLoadError(apiErrorMessage(error));
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		const handle = window.setTimeout(() => {
			listReviewResources({
				search: searchQuery,
				type: selectedFileType,
				status: selectedStatus,
				dateRange,
			})
				.then((rows) => {
					if (!cancelled) {
						setRecursos(rows);
						setLoadError(null);
					}
				})
				.catch((error: unknown) => {
					if (!cancelled) setLoadError(apiErrorMessage(error));
				});
		}, searchQuery ? 300 : 0);
		return () => {
			cancelled = true;
			window.clearTimeout(handle);
		};
	}, [searchQuery, selectedFileType, selectedStatus, dateRange]);

	const visibleRecursos = useMemo(() => {
		if (selectedMateria === 'all') return recursos;
		return recursos.filter((recurso) => recurso.subjectId === selectedMateria);
	}, [recursos, selectedMateria]);

	const reload = () => {
		listReviewResources({
			search: searchQuery,
			type: selectedFileType,
			status: selectedStatus,
			dateRange,
		})
			.then((rows) => {
				setRecursos(rows);
				setLoadError(null);
			})
			.catch((error: unknown) => setLoadError(apiErrorMessage(error)));
	};

	const changeStatus = async (
		recurso: IRecursoRevision,
		status: 'aprobado' | 'denegado' | 'cambios_solicitados',
		note?: string
	) => {
		try {
			await setResourceStatus(recurso.id, status, note);
			reload();
		} catch (error) {
			setLoadError(apiErrorMessage(error));
		}
	};

	return (
		<MainLayout
			pageTitle='Revisión de recursos'
			pageSubtitle='Revisa y aprueba los archivos subidos por los profesores'
		>
			<RevisionRecursosPage
				recursos={visibleRecursos}
				materias={materias}
				loadError={loadError}
				searchQuery={searchQuery}
				onSearchChange={(query) => dispatch(setSearchQuery(query))}
				selectedFileType={selectedFileType}
				onFileTypeChange={(fileType) => dispatch(setSelectedFileType(fileType))}
				selectedStatus={selectedStatus}
				onStatusChange={(status) => dispatch(setSelectedStatus(status))}
				selectedMateria={selectedMateria}
				onMateriaChange={(materia) => dispatch(setSelectedMateria(materia))}
				dateRange={dateRange}
				onDateRangeChange={(range) => dispatch(setDateRange(range))}
				onResetFilters={() => dispatch(resetFilters())}
				onDownload={(recurso) => window.open(resourceDownloadUrl(recurso.id), '_blank', 'noopener')}
				onDenegar={(recurso) => void changeStatus(recurso, 'denegado')}
				onSolicitarCambios={setChangesTarget}
				onAprobar={(recurso) => void changeStatus(recurso, 'aprobado')}
			/>
			<SolicitarCambiosDialog
				open={changesTarget !== null}
				resourceName={changesTarget?.name ?? ''}
				onClose={() => setChangesTarget(null)}
				onConfirm={(note) => {
					if (!changesTarget) return;
					void changeStatus(changesTarget, 'cambios_solicitados', note);
					setChangesTarget(null);
				}}
			/>
		</MainLayout>
	);
};

export default RecursosPage;
